import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { CalendarDays } from 'lucide-react'
import AppLayout from '../../components/layout/AppLayout'
import { useIsMobile } from '../../hooks/useBreakpoint'
import { mailApi, type AccountSummary, type MailFolder } from '../../services/mailApi'
import ConnectMailEmptyState from '../../components/integrations/ConnectMailEmptyState'
import MailTopBar from '@os/mail/web/components/MailTopBar'
import MailFolderSidebarWrapper from '../../modules/mail/MailFolderSidebarWrapper'
import type { MailSidebarView, SavedSearchItem } from '@os/mail/web/components/MailFolderSidebar'
import MailCategoryTabs, { CATEGORY_LABEL_ID, type CategoryTabId } from '@os/mail/web/components/MailCategoryTabs'
import MailThreadList, { type MailThreadListHandle } from '@os/mail/web/components/MailThreadList'
import MailThreadView, { type MailThreadMutation } from '@os/mail/web/components/MailThreadView'
import MailComposer from '@os/mail/web/components/MailComposer'
import MailDraftList, { type DraftRow } from '@os/mail/web/components/MailDraftList'
import MailSettingsModal from '@os/mail/web/components/MailSettingsModal'
import MailNotificationsBanner from '@os/mail/web/components/MailNotificationsBanner'
import MailAccountFilter from '@os/mail/web/components/MailAccountFilter'
import MailCalendarPanel from '@os/calendar/web/MailCalendarPanel'
import { MOCK_CLIENTS } from '@os/mail/web/components/MailClientFilter'
import MailManageLabelsModal, { readHiddenLabels } from '@os/mail/web/components/MailManageLabelsModal'
import MailShortcutsHelp from '@os/mail/web/components/MailShortcutsHelp'
import MailAdvancedSearch from '@os/mail/web/components/MailAdvancedSearch'
import MailSaveSearchDialog from '@os/mail/web/components/MailSaveSearchDialog'
import { parseSearch, hasOperators } from '@os/mail/web/components/searchOperators'
import { useMailShortcuts } from '@os/mail/web/components/useMailShortcuts'
import { useToastStore } from '../../stores/toastStore'
import { subscribeMailEvents, type MailEventChanged } from '../../services/mailSocket'
import MailConnectingModal from '../../modules/mail/MailConnectingModal'
import MailSentToast from '../../components/mail/MailSentToast'
import { usePendingMailSendStore, type PendingSendSnapshot } from '../../stores/pendingMailSendStore'

// OSPROD-MAIL-CAL-L4: real-data Mail dashboard. Three-pane layout
// (sidebar | thread list | thread view). Empty-state from L3 when
// the user has no connected accounts. The colourful KpiRow,
// VisionRibbon, and AI badges from the previous mock are gone —
// single primary accent with the gold star is the only chrome.

const FOLDER_ORDER: MailFolder[] = ['inbox', 'starred', 'sent', 'drafts', 'spam', 'trash']

export default function MailDashboard() {
  // W1.3: drives the mobile (<1024) layout — the folder/label sidebar moves
  // into the shell's L2 nav drawer and the reading pane becomes a full-screen
  // overlay. Desktop (>=1024) is unchanged.
  const isMobile = useIsMobile()
  const [accounts, setAccounts] = useState<AccountSummary[] | null>(null)
  // Phase 6: `view` covers MailFolder + the synthetic 'snoozed'
  // virtual folder. `folder` (the explicit MailFolder) is derived
  // — when view === 'snoozed' we still need a real folder for the
  // shortcut handlers (j/k navigation between standard folders).
  const [view, setView] = useState<MailSidebarView>('inbox')
  const folder: MailFolder = view === 'snoozed' ? 'inbox' : view
  const snoozedMode = view === 'snoozed'
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  // Phase 6: account filter for the thread list. null = "All accounts".
  const [activeAccountId, setActiveAccountId] = useState<number | null>(null)
  // Inbox-only category tab (Primary / Promotions / Social / Updates /
  // Forums). Resets to Primary whenever the user navigates to a
  // non-inbox folder or drills into a label — Gmail's pattern.
  const [categoryTab, setCategoryTab] = useState<CategoryTabId>('primary')
  const [labels, setLabels] = useState<string[]>([])
  // OSPROD-MAIL-USER-LABELS: our-side labels with persisted colour.
  // Sidebar renders them above Gmail labels and the dashboard
  // routes clicks into the user-label thread filter.
  const [userLabels, setUserLabels] = useState<Array<{ id: string; name: string; color: string }>>([])
  const [activeUserLabelId, setActiveUserLabelId] = useState<string | null>(null)
  const [savedSearches, setSavedSearches] = useState<SavedSearchItem[]>([])
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  // OSPROD-MAIL-EXPAND: thread pane can be expanded to take the
  // whole right area (thread list collapses) so a long thread
  // reads without horizontal cramping. Resets when the user
  // navigates back out of a thread.
  const [threadExpanded, setThreadExpanded] = useState(false)
  // Mail calendar side panel (Gmail-style). Toggle persists across
  // reloads. Desktop-only — hidden on mobile where the reading pane
  // is already a full-screen overlay.
  const [showCalendarPanel, setShowCalendarPanel] = useState<boolean>(() => {
    try { return localStorage.getItem('mail.calendarPanel.open') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('mail.calendarPanel.open', showCalendarPanel ? '1' : '0') } catch { /* ignore */ }
  }, [showCalendarPanel])
  // OSPROD-MAIL-CLIENT: client-side mock filter. Picks a "client"
  // (account/customer) and narrows the visible threads. The list
  // is mocked here for now — the under-the-hood plumbing is the
  // existing search infra (we just inject `from:` operator into
  // the search query when a client is picked, so the thread list
  // already filters on it without a new endpoint).
  const [activeClientId, setActiveClientId] = useState<string | null>(null)
  // OSPROD-MAIL-CUSTOM-TABS: user-defined tabs persisted to
  // localStorage. No backend yet — picking one shows a friendly
  // empty state per the spec ("пока пусть пусто там все будет").
  const [customTabs, setCustomTabs] = useState<Array<{ id: string; label: string }>>(() => {
    try {
      const raw = localStorage.getItem('mail:customTabs')
      return raw ? JSON.parse(raw) : []
    } catch { return [] }
  })
  const [activeCustomTabId, setActiveCustomTabId] = useState<string | null>(null)
  const persistCustomTabs = (next: Array<{ id: string; label: string }>) => {
    setCustomTabs(next)
    try { localStorage.setItem('mail:customTabs', JSON.stringify(next)) } catch { /* ignore */ }
  }
  const [composeOpen, setComposeOpen] = useState(false)
  // When set, the composer opens preloaded with an existing draft —
  // body, recipients, subject, and the draft cuid so auto-save
  // continues updating the same row (not creating a new one). null
  // means a blank new compose. Cleared on close.
  const [composeInitial, setComposeInitial] = useState<{
    accountId?: string
    to: string[]
    cc?: string[]
    bcc?: string[]
    subject: string
    bodyText: string
    bodyHtml?: string
    inReplyToMessageId?: string
    threadId?: string
    draftId?: string
  } | null>(null)
  const [draftListRefreshKey, setDraftListRefreshKey] = useState(0)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [manageLabelsOpen, setManageLabelsOpen] = useState(false)
  // OSPROD-MAIL-LABELS: hidden-from-sidebar set persisted in
  // localStorage by the Manage Labels modal. Re-read on every
  // mutation so the sidebar reflects toggles without a full reload.
  const [hiddenLabels, setHiddenLabels] = useState<string[]>(() => readHiddenLabels())
  const [helpOpen, setHelpOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [saveSearchOpen, setSaveSearchOpen] = useState(false)
  // OSPROD-MAIL-CONNECT-MODAL: shown after the Google OAuth
  // redirect-back (?connected=ok&accountId=…). Holds the just-
  // connected accountId so the modal can poll its sync flag.
  // null when no connect flow is in-flight.
  const [connectingModal, setConnectingModal] = useState<{ accountId: string } | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const schedulePendingSend = usePendingMailSendStore((s) => s.schedule)
  const lastShown = useRef<Map<string, number>>(new Map())
  const threadListRef = useRef<MailThreadListHandle | null>(null)
  const showToast = useToastStore((s) => s.showToast)

  // Gmail-style Send-with-Undo: composer fires this with the body
  // snapshot, we close it and hand the 10s window to the toast.
  // After 10s the timer in pendingMailSendStore fires the actual
  // send. Undo on the toast reopens the composer with the snapshot.
  const handleSendRequested = useCallback((snap: PendingSendSnapshot) => {
    schedulePendingSend(snap, 10_000, async (s) => {
      const hasAttachments = !!s.attachments?.length
      const body = {
        accountId: s.accountId as any,
        to: s.to,
        cc: s.cc,
        bcc: s.bcc,
        subject: s.subject,
        bodyText: s.bodyText,
        bodyHtml: s.bodyHtml,
        inReplyToMessageId: s.inReplyToMessageId,
        threadId: s.threadId,
        ...(hasAttachments ? { attachments: s.attachments } : {}),
      }
      try {
        // OSPROD-163: drafts are text-only, so when the user queued
        // attachments we send directly (embedding them) and drop any
        // orphaned text draft instead of going through gmail.drafts.send.
        if (s.draftId && !hasAttachments) {
          // Update + send via the draft path so gmail.drafts.send
          // naturally consumes the gmail draft — no orphan in /Drafts.
          // The intermediate updateDraft makes sure gmail's copy has
          // the latest body even if the autosave was still in-flight
          // when the user clicked Send.
          await mailApi.updateDraft(s.draftId, body)
          await mailApi.sendDraft(s.draftId)
        } else {
          // No draft (or attachments queued) -- direct send. If a
          // text-only draft was auto-saved, delete it so it doesn't
          // linger in /Drafts after the real message goes out.
          await mailApi.send(body)
          if (s.draftId) {
            try { await mailApi.deleteDraft(s.draftId) } catch { /* best-effort */ }
          }
        }
        // Kick a sync so the SENT message lands in the local mirror
        // within seconds and the open thread reflects it on reload.
        mailApi.sync().catch(() => { /* best-effort */ })
        setRefreshKey((k) => k + 1)
        setDraftListRefreshKey((k) => k + 1)
      } catch (err: any) {
        showToast({
          id: `mail-send-error-${Date.now()}`,
          type: 'error',
          title: 'Не удалось отправить письмо',
          body: err?.response?.data?.error || err?.message || 'Попробуйте ещё раз.',
        })
      }
    })
  }, [schedulePendingSend, showToast])

  // Undo from the toast — restore the snapshot into the composer.
  // The toast itself has already cancelled the pending timer; we
  // just have to put the user back in front of their text.
  const handleUndoSend = useCallback((snap: PendingSendSnapshot) => {
    setComposeInitial({
      accountId: String(snap.accountId),
      to: snap.to,
      cc: snap.cc && snap.cc.length > 0 ? snap.cc : undefined,
      bcc: snap.bcc && snap.bcc.length > 0 ? snap.bcc : undefined,
      subject: snap.subject,
      bodyText: snap.bodyText,
      bodyHtml: snap.bodyHtml,
      inReplyToMessageId: snap.inReplyToMessageId,
      threadId: snap.threadId,
      draftId: snap.draftId ?? undefined,
    })
    setComposeOpen(true)
  }, [])

  useMailShortcuts({
    onNextThread: () => threadListRef.current?.selectNext(),
    onPrevThread: () => threadListRef.current?.selectPrev(),
    onNextFolder: () => {
      const idx = FOLDER_ORDER.indexOf(folder)
      setView(FOLDER_ORDER[(idx + 1) % FOLDER_ORDER.length])
      setActiveLabel(null); setSelectedThreadId(null)
    },
    onPrevFolder: () => {
      const idx = FOLDER_ORDER.indexOf(folder)
      setView(FOLDER_ORDER[(idx - 1 + FOLDER_ORDER.length) % FOLDER_ORDER.length])
      setActiveLabel(null); setSelectedThreadId(null)
    },
    onOpenComposer: () => setComposeOpen(true),
    onShowHelp: () => setHelpOpen(true),
    onCloseModal: () => {
      if (helpOpen) setHelpOpen(false)
      else if (advancedOpen) setAdvancedOpen(false)
      else if (saveSearchOpen) setSaveSearchOpen(false)
      else if (settingsOpen) setSettingsOpen(false)
      else if (composeOpen) setComposeOpen(false)
      else if (selectedThreadId) setSelectedThreadId(null)
    },
    onFocusSearch: () => {
      // Search input now lives on the top app bar; placeholder still
      // starts with "Search" so the same selector grabs it.
      const el = document.querySelector<HTMLInputElement>('input[placeholder^="Search"]')
      el?.focus()
    },
    // TODO: expose reply trigger from MailThreadView for r
  })

  const reloadAccounts = useCallback(() => {
    mailApi.listAccounts()
      .then((r) => setAccounts(r.data.accounts))
      .catch(() => setAccounts([]))
  }, [])

  const reloadLabels = useCallback(() => {
    mailApi.listLabels()
      .then((r) => {
        setLabels(r.data.labels)
        setUserLabels(r.data.userLabels ?? [])
      })
      .catch(() => { setLabels([]); setUserLabels([]) })
  }, [])

  const reloadSavedSearches = useCallback(() => {
    mailApi.listSavedSearches()
      .then((r) => setSavedSearches(r.data.savedSearches.map((s) => ({ id: s.id, name: s.name, query: s.query }))))
      .catch(() => setSavedSearches([]))
  }, [])

  useEffect(() => {
    reloadAccounts()
    reloadLabels()
    reloadSavedSearches()
  }, [reloadAccounts, reloadLabels, reloadSavedSearches])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('connected') === 'ok') {
      // Replace the previous 3-second toast with a sticky modal
      // that shows live sync progress and auto-closes when the
      // server flips MailAccount.initialSyncDone:true. The toast
      // flashed and vanished while the backfill was still running,
      // leaving the user staring at an empty inbox — see
      // MailConnectingModal.tsx for the rationale.
      const accountId = params.get('accountId')
      if (accountId) setConnectingModal({ accountId })
      window.history.replaceState({}, '', window.location.pathname)
      reloadAccounts()
      reloadLabels()
    }
    if (params.get('connected') === 'err') {
      const reason = params.get('reason') ?? 'unknown'
      showToast({
        id: `mail-connect-err-${Date.now()}`,
        type: 'error',
        title: "Couldn't connect Gmail",
        body: `Reason: ${reason}. Try again or contact support.`,
      })
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [reloadAccounts, reloadLabels, showToast])

  // OSPROD-MAIL-AUTOSYNC: periodically pulse the sync endpoint so
  // new mail arrives even when Python's push watch hasn't fired. The
  // refresh button does this on demand; this is the background tick.
  //
  // Cadence: every 60s while the tab is visible. Stops when the tab
  // is hidden (page-visibility) so we don't burn server effort on
  // backgrounded browsers. Resumes immediately on visibility-return
  // with an opportunistic single tick — that way switching back to
  // the tab feels fresh, instead of waiting up to 60s for the timer.
  useEffect(() => {
    if (!accounts || accounts.length === 0) return
    const POLL_MS = 60_000
    let timer: ReturnType<typeof setInterval> | null = null
    let stopped = false

    const tickOnce = async () => {
      if (stopped) return
      try {
        await mailApi.sync()
        // The socket-driven coalescer in the effect above handles the
        // re-fetch when Python emits new mail. We don't bump refreshKey
        // here — that would double-fetch on every tick even when
        // nothing changed.
      } catch {
        /* best-effort — refresh button is the manual fallback */
      }
    }

    const start = () => {
      if (timer) return
      timer = setInterval(tickOnce, POLL_MS)
    }
    const stop = () => {
      if (!timer) return
      clearInterval(timer)
      timer = null
    }

    const onVisibility = () => {
      if (document.hidden) {
        stop()
      } else {
        tickOnce() // opportunistic catch-up
        start()
      }
    }

    if (!document.hidden) {
      tickOnce()
      start()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stopped = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [accounts])

  useEffect(() => {
    // Coalesce a stream of mail:event:changed frames into ONE refresh
    // + at-most-one toast per BURST_WINDOW_MS. The historical Gmail
    // backfill that runs right after Connect Google fires hundreds of
    // mail:new events — without coalescing each one bumped refreshKey
    // (re-firing /api/mail/threads in a tight loop) and stacked a
    // toast in the corner. This collapses the burst to a single refetch
    // and at most one toast per window. If more than SYNC_BURST_THRESHOLD
    // events fire in a window we treat the burst as a sync flush and
    // suppress the toast entirely — UX-wise the user just connected
    // their mailbox, the dashboard will fill in on its own; "47 new
    // mails!" right after consent is noise, not a notification.
    const BURST_WINDOW_MS = 2000
    const SYNC_BURST_THRESHOLD = 5
    let pending: MailEventChanged | null = null
    let burstCount = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    const flush = () => {
      timer = null
      const count = burstCount
      const last = pending
      pending = null
      burstCount = 0
      // Gmail-style: slide the new/updated thread into the list IN PLACE
      // (page 1 re-fetch merged by id — new threads prepend to the first
      // rows, existing rows refresh) WITHOUT blanking the list or resetting
      // scroll. The old `setRefreshKey(k+1)` drove MailThreadList through
      // load('reset') → setThreads(null) → a full "Loading…" redraw that
      // nuked the scroll position on every single new mail (the "таблица
      // перерисовывается постоянно" complaint). If the list isn't mounted
      // (e.g. the Drafts view) this is a no-op — it loads fresh on remount.
      threadListRef.current?.mergeRefresh()
      if (!last || last.kind !== 'new') return
      if (count >= SYNC_BURST_THRESHOLD) return // sync flush — silent
      const now = Date.now()
      const debounceKey = last.threadId ? `t:${last.threadId}` : `m:${last.messageId}`
      const lastAt = lastShown.current.get(debounceKey) ?? 0
      if (now - lastAt < 5000) return
      lastShown.current.set(debounceKey, now)
      // New-mail toast + browser notification intentionally suppressed (user
      // request): the thread list already updates live via mergeRefresh above,
      // so the popups were just noise. The debounce bookkeeping is kept so the
      // notifications can be re-enabled here without reworking the coalescing.
    }

    const unsub = subscribeMailEvents((e) => {
      pending = e
      burstCount += 1
      if (timer) clearTimeout(timer)
      timer = setTimeout(flush, BURST_WINDOW_MS)
    })
    return () => {
      unsub()
      if (timer) clearTimeout(timer)
    }
  }, [])

  // Gmail-faithful layout: top app bar → [sidebar | (tabs + thread
  // list) | thread view]. Sidebar owns the Compose CTA; top bar
  // owns search; tabs sit above the thread list and apply only when
  // the user is on the Inbox folder without a label drill-down.
  // Snoozed/spam/trash views and label drill-downs hide the tabs.
  //
  // Hooks MUST run before the early returns below — when `accounts`
  // flipped from null → loaded, the previous ordering ran zero
  // useMemos on the loading render and two on the loaded one,
  // tripping React #310 ("Rendered more hooks than during the
  // previous render"). The whole top of the function is now hook-
  // safe; the conditional returns only short-circuit rendering,
  // not hook registration.
  const tabsVisible = view === 'inbox' && !activeLabel
  // Compose the effective `activeLabel` passed to the thread list:
  // explicit label drill > category tab on inbox > none. Tabs collapse
  // to a `?label=CATEGORY_*` filter the existing list endpoint
  // already understands; Primary maps to "no extra filter".
  const effectiveLabel = useMemo(() => {
    if (activeLabel) return activeLabel
    if (tabsVisible) return CATEGORY_LABEL_ID[categoryTab] ?? null
    return null
  }, [activeLabel, tabsVisible, categoryTab])

  // Phase 7: parse the raw search string into structured operators
  // once per change. The thread-list filters off the structured
  // shape; the topbar chip rail renders off the same parsed object,
  // so what the user sees in chips is exactly what's filtered.
  //
  // OSPROD-MAIL-CLIENT: when a client is picked from the topbar
  // dropdown, append its email domains as @-tokens onto the free-
  // text portion of the parsed query. The server's existing full-
  // text search already covers from / subject / body, so
  // "@nyulangone.org" naturally narrows the inbox to that client
  // — no new endpoint, no new column, no new param. Once the real
  // Clients module ships we'll swap to a dedicated `clientId`
  // param without touching the UI.
  const parsedSearch = useMemo(() => {
    const base = parseSearch(searchQuery)
    if (activeClientId) {
      const c = MOCK_CLIENTS.find((x) => x.id === activeClientId)
      if (c && c.domains.length > 0) {
        const tokens = c.domains.map((d) => `@${d}`).join(' ')
        base.text = base.text ? `${base.text} ${tokens}`.trim() : tokens
      }
    }
    return base
  }, [searchQuery, activeClientId])
  const showOperatorChips = hasOperators(parsedSearch) || searchQuery.trim().length > 0 || activeClientId !== null

  if (accounts === null) {
    return (
      <AppLayout>
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      </AppLayout>
    )
  }

  if (accounts.length === 0) {
    // Connect-account screen runs without the workspace secondary sidebar
    // — the user is here to wire up their inbox, not to dashboard-hop.
    return (
      <AppLayout>
        <ConnectMailEmptyState onConnected={reloadAccounts} />
      </AppLayout>
    )
  }

  // W1.3: one sidebar element, mounted in exactly one place per breakpoint —
  // the shell's L2 drawer on mobile, inline in the section on desktop.
  const folderSidebar = (
    <MailFolderSidebarWrapper
      view={view}
      activeLabel={activeLabel}
      labels={labels.filter((l) => !hiddenLabels.includes(l))}
      userLabels={userLabels}
      activeUserLabelId={activeUserLabelId}
      onPickUserLabel={(id) => {
        setActiveUserLabelId(id)
        setActiveLabel(null)
        setSelectedThreadId(null)
        setCategoryTab('primary')
      }}
      unreadCount={undefined /* TODO: surface from /api/mail/unread-counts when route lands */}
      onCompose={() => setComposeOpen(true)}
      onPickView={(v) => {
        setView(v); setActiveLabel(null); setActiveUserLabelId(null); setSelectedThreadId(null); setCategoryTab('primary')
      }}
      onPickLabel={(l) => {
        setActiveLabel(l); setActiveUserLabelId(null); setSelectedThreadId(null); setCategoryTab('primary')
      }}
      savedSearches={savedSearches}
      onPickSavedSearch={(s) => {
        // Loading a saved search jumps to the inbox view (saved
        // searches don't bind to a folder) and applies the
        // stored query. Selection clears so the right-pane
        // doesn't keep stale context.
        setView('inbox'); setActiveLabel(null); setSelectedThreadId(null); setCategoryTab('primary')
        setSearchQuery(s.query)
      }}
      onDeleteSavedSearch={async (id) => {
        try { await mailApi.deleteSavedSearch(id) } catch { /* best-effort */ }
        reloadSavedSearches()
      }}
      // OSPROD-MAIL-CLIENT-RAIL: clients list + active state +
      // pick handler. Behaviour unchanged from the old top-bar
      // dropdown — picking a client narrows the search query
      // via MOCK_CLIENTS domains.
      activeClientId={activeClientId}
      onPickClient={(id) => { setActiveClientId(id); setSelectedThreadId(null) }}
      onManageLabels={() => setManageLabelsOpen(true)}
    />
  )

  return (
    <AppLayout secondarySidebar={isMobile ? folderSidebar : undefined}>
      <MailTopBar
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onSettingsClick={() => setSettingsOpen(true)}
        parsedSearch={parsedSearch}
        onAdvancedClick={() => setAdvancedOpen(true)}
        onSaveSearchClick={showOperatorChips ? () => setSaveSearchOpen(true) : undefined}
        rightSlot={
          // OSPROD-MAIL-CLIENT-RAIL: Clients dropdown moved to the
          // left sidebar (under Labels). Only the per-account filter
          // stays in the top bar — it's a less common drilldown so
          // the discoverability cost is fine, and it groups with
          // other "metadata" search-style filters on the top.
          <div className="flex items-center gap-2">
            <MailAccountFilter
              accounts={accounts}
              activeAccountId={activeAccountId}
              onChange={(id) => { setActiveAccountId(id); setSelectedThreadId(null) }}
            />
            {!isMobile && (
              <button
                type="button"
                onClick={() => setShowCalendarPanel((v) => !v)}
                className={`h-8 w-8 flex items-center justify-center rounded-md transition ${showCalendarPanel ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                aria-label="Toggle calendar panel"
                title="Calendar"
              >
                <CalendarDays className="h-4 w-4" />
              </button>
            )}
          </div>
        }
      />
      <MailNotificationsBanner />
      <section className="flex-1 flex min-h-0 bg-card overflow-hidden">
        {/* W1.3: desktop renders the folder rail inline; on mobile it lives
            in the shell's L2 nav drawer (see secondarySidebar on AppLayout). */}
        {!isMobile && folderSidebar}
        <div className="flex-1 min-w-0 flex flex-col lg:border-l lg:border-border bg-background">
          {tabsVisible && (
            <MailCategoryTabs
              active={categoryTab}
              onChange={(t) => { setCategoryTab(t); setActiveCustomTabId(null); setSelectedThreadId(null) }}
              customTabs={customTabs}
              activeCustomTabId={activeCustomTabId}
              onPickCustomTab={(id) => { setActiveCustomTabId(id); setSelectedThreadId(null) }}
              onAddCustomTab={(label) => {
                const id = `custom-${Date.now().toString(36)}`
                persistCustomTabs([...customTabs, { id, label }])
              }}
              onDeleteCustomTab={(id) => {
                persistCustomTabs(customTabs.filter((t) => t.id !== id))
                if (activeCustomTabId === id) setActiveCustomTabId(null)
              }}
            />
          )}
          <div className="flex-1 min-h-0 flex">
            {/* OSPROD-MAIL-EXPAND: while the thread pane is
                expanded, hide the list entirely (Gmail's
                "thread takes the whole right area" mode).
                OSPROD-MAIL-CUSTOM-TABS: when the user picks a
                custom tab — which has no backing query yet —
                show a friendly placeholder where the list
                would be. Per the spec "пока пусть пусто там
                все будет" this is intentional. */}
            {!threadExpanded && (
              activeCustomTabId ? (
                <div className="flex-1 min-w-0 flex flex-col items-center justify-center text-center px-6 py-12 text-muted-foreground">
                  <div className="text-foreground text-base font-medium mb-1">
                    {customTabs.find((t) => t.id === activeCustomTabId)?.label ?? 'Custom tab'}
                  </div>
                  <p className="text-[12.5px] max-w-xs">
                    Custom tabs don't have a query wired up yet. They're a placeholder for upcoming saved-view rules.
                  </p>
                </div>
              ) : view === 'drafts' ? (
                // Drafts folder reads MailDraft rows directly — see
                // MailDraftList for why we don't go through the
                // threads list path for this folder.
                <MailDraftList
                  refreshKey={draftListRefreshKey}
                  onOpen={(d: DraftRow) => {
                    setComposeInitial({
                      accountId: d.accountId,
                      to: d.to,
                      cc: d.cc.length > 0 ? d.cc : undefined,
                      bcc: d.bcc.length > 0 ? d.bcc : undefined,
                      subject: d.subject,
                      bodyText: d.bodyText,
                      bodyHtml: d.bodyHtml ?? undefined,
                      inReplyToMessageId: d.inReplyToMessageId ?? undefined,
                      threadId: d.threadId ?? undefined,
                      draftId: d.id,
                    })
                    setComposeOpen(true)
                  }}
                />
              ) : (
                <MailThreadList
                  ref={threadListRef}
                  folder={folder}
                  activeLabel={effectiveLabel}
                  userLabelId={activeUserLabelId}
                  selectedThreadId={selectedThreadId}
                  onSelect={setSelectedThreadId}
                  refreshKey={refreshKey}
                  searchQuery={searchQuery}
                  accountId={activeAccountId}
                  snoozedMode={snoozedMode}
                  parsedSearch={parsedSearch}
                />
              )
            )}
            {selectedThreadId && (
              // OSPROD-MAIL-LAYOUT: `min-h-0` is mandatory here.
              // Without it the flex-col parent doesn't constrain the
              // child's height and the inner `flex-1 overflow-y-auto`
              // in MailThreadView never engages — the message list
              // grows past the viewport and the reply-chips at the
              // bottom (plus everything beyond the first message)
              // simply stay clipped off-screen with no scrollbar.
              // OSPROD-MAIL-EXPAND: when the user clicks the
              // expand button in MailThreadView's toolbar, the
              // thread pane fills the entire space the thread
              // list also occupies (right pane only — Inbox rail
              // stays visible per the user's spec).
              // W1.3: on mobile the reading pane is a full-screen overlay over
              // the list (it has its own back button); on desktop it stays the
              // inline 55% / expanded pane. `lg:static` makes the base inset-0
              // inert >=1024, so desktop rendering is unchanged.
              <div className={`fixed inset-0 z-30 lg:static lg:z-auto min-w-0 min-h-0 lg:border-l lg:border-border flex flex-col bg-card ${threadExpanded ? 'lg:flex-1' : 'lg:w-[55%]'}`}>
                <MailThreadView
                  threadId={selectedThreadId}
                  accounts={accounts}
                  expanded={threadExpanded}
                  onToggleExpanded={() => setThreadExpanded((v) => !v)}
                  onMutated={(mutation?: MailThreadMutation) => {
                    reloadLabels()
                    // Reflect the reading-pane action in the thread list IN
                    // PLACE (no full refetch / blank / scroll reset). Each
                    // action maps to an optimistic list mutation so the row
                    // reacts instantly — star flips, read/unread badge flips,
                    // and deleted/archived/spam/snoozed rows vanish on the
                    // spot instead of lingering until a manual refresh.
                    const id = selectedThreadId
                    if (!id) return
                    switch (mutation?.type) {
                      case 'removed':
                        threadListRef.current?.removeThread(id)
                        // It's gone — close the now-empty reading pane too so
                        // the user isn't staring at a trashed/archived thread.
                        setSelectedThreadId(null)
                        setThreadExpanded(false)
                        break
                      case 'starred':
                        threadListRef.current?.setStarred(id, mutation.starred)
                        break
                      case 'unread':
                        threadListRef.current?.markUnread(id)
                        break
                      case 'read':
                        threadListRef.current?.markRead(id)
                        break
                      default:
                        // labels / unknown → re-sync the row in place.
                        threadListRef.current?.mergeRefresh()
                    }
                  }}
                  onClose={() => { setSelectedThreadId(null); setThreadExpanded(false) }}
                  // OS is a single-tenant business workspace — every sender
                  // is a vetted business contact, so the "Show images" gate
                  // is friction. @os/mail 1.13.3+ exposes defaultShowImages;
                  // we opt in so external images render without the
                  // per-message click. Per-message toggle still works for
                  // case-by-case opt-out.
                  defaultShowImages={true}
                  // Gmail-style Send-with-Undo for inline replies.
                  onSendRequested={handleSendRequested}
                />
              </div>
            )}
          </div>
        </div>
        {showCalendarPanel && !isMobile && (
          <div className="hidden lg:flex w-[320px] shrink-0 border-l border-border bg-card">
            <MailCalendarPanel />
          </div>
        )}
      </section>
      {composeOpen && (
        <MailComposer
          accounts={accounts}
          defaultAccountId={composeInitial?.accountId
            // Pin the From account to the draft's account so the
            // existing MailDraft row is updated under the correct
            // mailbox. Otherwise pick primary / first.
            ?? accounts.find((a) => a.isPrimary)?.id
            ?? accounts[0]?.id}
          initial={composeInitial ?? { to: [], subject: '', bodyText: '' }}
          onClose={() => {
            // If this composer was a reply / a draft tied to a thread, reveal
            // that thread's card after closing (instead of a blank list) so the
            // user lands back where they were. Capture before clearing initial.
            const tid = composeInitial?.threadId ?? null
            setComposeOpen(false)
            setComposeInitial(null)
            // The draft may have been edited or deleted while open —
            // refresh the Drafts list so the row reflects current
            // state (subject/preview/timestamp) on the next visit.
            setDraftListRefreshKey((k) => k + 1)
            if (tid) setSelectedThreadId(tid)
          }}
          onSent={() => {
            setComposeOpen(false)
            setComposeInitial(null)
            setDraftListRefreshKey((k) => k + 1)
            reloadLabels()
          }}
          // Gmail-style Send-with-Undo for modal compose.
          onSendRequested={handleSendRequested}
        />
      )}
      {settingsOpen && (
        <MailSettingsModal
          accounts={accounts}
          onClose={() => setSettingsOpen(false)}
          onAccountsChanged={() => {
            // An account was disconnected/purged — drop the open thread,
            // refresh the account + label lists (may flip to the connect
            // empty state when the last account is gone) and re-fetch the
            // thread list.
            setSettingsOpen(false)
            setSelectedThreadId(null)
            reloadAccounts()
            reloadLabels()
            setRefreshKey((k) => k + 1)
          }}
        />
      )}
      {manageLabelsOpen && (
        <MailManageLabelsModal
          gmailLabels={labels}
          onClose={() => setManageLabelsOpen(false)}
          onMutated={() => {
            setHiddenLabels(readHiddenLabels())
            reloadLabels()
            // The user just edited labels/rules — bump refreshKey
            // so the thread list re-runs eval and surfaces new tags.
            setRefreshKey((k) => k + 1)
          }}
        />
      )}
      {helpOpen && <MailShortcutsHelp onClose={() => setHelpOpen(false)} />}
      {advancedOpen && (
        <MailAdvancedSearch
          initial={parsedSearch}
          onCancel={() => setAdvancedOpen(false)}
          onApply={(q) => { setSearchQuery(q); setAdvancedOpen(false) }}
        />
      )}
      {saveSearchOpen && (
        <MailSaveSearchDialog
          query={searchQuery}
          onCancel={() => setSaveSearchOpen(false)}
          onSave={async (name) => {
            await mailApi.createSavedSearch(name, searchQuery)
            reloadSavedSearches()
            setSaveSearchOpen(false)
            showToast({ id: `search-saved-${Date.now()}`, type: 'message', title: 'Search saved' })
          }}
        />
      )}
      {connectingModal && (
        <MailConnectingModal
          accountId={connectingModal.accountId}
          onClose={() => {
            setConnectingModal(null)
            // Re-fetch accounts on close so the dashboard reflects
            // any changes the worker made during the sync (display
            // name, status, etc) without waiting for the next page
            // navigation.
            reloadAccounts()
          }}
        />
      )}
      {/* Non-blocking Send-with-Undo snackbar (Gmail-style).
          Always mounted; renders only when pendingMailSendStore has
          a scheduled send. Undo restores composeInitial + reopens
          the composer. */}
      <MailSentToast onUndo={handleUndoSend} />
    </AppLayout>
  )
}
